import {
  IAddressSchema,
  IContactRespSchema,
  IPaymentMethodContactSchema,
  QuoteSettingsDefaultAddressSourceEnum,
  QuoteShareContactRequiredAddress,
  QuoteShareContactsForm,
  QuoteShareForm,
  QuoteSharePaymentMethodForm,
  QuoteShareResponse,
} from '@monetize/types/app';
import { QuoteShareFieldVisibilityEnum } from '@monetize/types/experience-config';
import { getCountryCodeFromNameOrCode } from '../../services/countryState';

export enum AllowedPaymentMethods {
  ELECTRONIC = 'ELECTRONIC',
  CREDIT_CARD = 'CREDIT_CARD',
  DIRECT_DEBIT = 'DIRECT_DEBIT',
  ELECTRONIC_AND_ACH = 'ELECTRONIC_AND_ACH',
  ACH_WIRE_TRANSFER = 'ACH_WIRE_TRANSFER',
}

export interface QuoteShareCompanyDetails {
  /** Whether the combined Company and Order Details card should render at all. */
  showCard: boolean;
  /** Whether the card should be clickable (any field is editable). */
  cardEditable: boolean;
  showAccountName: boolean;
  accountNameEditable: boolean;
  showPo: boolean;
  poEditable: boolean;
  poRequired: boolean;
  showVat: boolean;
  vatEditable: boolean;
  vatRequired: boolean;
  /** Required-and-empty flags used to block quote acceptance. */
  isMissingRequiredPo: boolean;
  isMissingRequiredVat: boolean;
}

const isEditableVisibility = (visibility: QuoteShareFieldVisibilityEnum) =>
  visibility === QuoteShareFieldVisibilityEnum.EDITABLE ||
  visibility === QuoteShareFieldVisibilityEnum.EDITABLE_REQUIRED;

/**
 * Derives how the Legal Name, Purchase Order Number, and VAT Number fields
 * behave in the combined "Company and Order Details" card on the Quote Share
 * page.
 *
 * PO/VAT are gated by both their backend flag (`allowPoNumber` /
 * `allowVatNumber`) AND their configured visibility. The account name is
 * governed solely by `allowAccountNameUpdate` (it is only shown when editable).
 */
export function resolveQuoteShareCompanyDetails({
  allowAccountNameUpdate,
  poVisibility,
  vatVisibility,
  allowPoNumber,
  allowVatNumber,
  purchaseOrderNumber,
  vatNumber,
}: {
  allowAccountNameUpdate: boolean;
  poVisibility: QuoteShareFieldVisibilityEnum;
  vatVisibility: QuoteShareFieldVisibilityEnum;
  allowPoNumber: boolean;
  allowVatNumber: boolean;
  purchaseOrderNumber?: string | null;
  vatNumber?: string | null;
}): QuoteShareCompanyDetails {
  const showAccountName = allowAccountNameUpdate;

  const showPo =
    allowPoNumber && poVisibility !== QuoteShareFieldVisibilityEnum.HIDDEN;
  const showVat =
    allowVatNumber && vatVisibility !== QuoteShareFieldVisibilityEnum.HIDDEN;

  const poEditable = showPo && isEditableVisibility(poVisibility);
  const vatEditable = showVat && isEditableVisibility(vatVisibility);

  const poRequired =
    showPo && poVisibility === QuoteShareFieldVisibilityEnum.EDITABLE_REQUIRED;
  const vatRequired =
    showVat &&
    vatVisibility === QuoteShareFieldVisibilityEnum.EDITABLE_REQUIRED;

  return {
    showCard: showAccountName || showPo || showVat,
    cardEditable: showAccountName || poEditable || vatEditable,
    showAccountName,
    accountNameEditable: showAccountName,
    showPo,
    poEditable,
    poRequired,
    showVat,
    vatEditable,
    vatRequired,
    isMissingRequiredPo: poRequired && !purchaseOrderNumber?.trim(),
    isMissingRequiredVat: vatRequired && !vatNumber?.trim(),
  };
}

/**
 * Copy shown before/while accepting a quote that requires eSign. The share page
 * is anonymous — the viewer is not necessarily the signer — so name the signer
 * and their email explicitly rather than saying "your email".
 */
export function getEsignRecipientMessage(
  esignContact?: IContactRespSchema | null,
): string {
  const email = esignContact?.email?.trim();
  if (!email) {
    return "After accepting, you'll complete the eSign process via email.";
  }
  const fullName = esignContact?.fullName?.trim();
  const recipient = fullName ? `${fullName} (${email})` : email;
  return `After accepting, the eSign request will be emailed to ${recipient}.`;
}

export const electronicPaymentMethods = new Set([
  AllowedPaymentMethods.ELECTRONIC,
  AllowedPaymentMethods.CREDIT_CARD,
  AllowedPaymentMethods.DIRECT_DEBIT,
  AllowedPaymentMethods.ELECTRONIC_AND_ACH,
]);

export const achPaymentMethods = new Set([
  AllowedPaymentMethods.ACH_WIRE_TRANSFER,
  AllowedPaymentMethods.ELECTRONIC_AND_ACH,
]);

function isMissingAddressFields(address?: IAddressSchema | null): boolean {
  if (!address) {
    return true;
  }
  return (
    !address.line1 || !address.city || !address.postalCode || !address.country
  );
}

export function isMissingRequiredAddress(
  quote?: QuoteShareResponse['quote'],
): boolean {
  if (!quote) {
    return true;
  }
  const isAccountBased =
    quote.addressSource === QuoteSettingsDefaultAddressSourceEnum.ACCOUNT;

  const billing = isAccountBased ? quote.billingAddress : quote.billingContact;
  const shipping = isAccountBased
    ? quote.shippingAddress
    : quote.shippingContact;

  return isMissingAddress(billing) || isMissingAddress(shipping);
}

export function isMissingAddress(
  contactOrAddress?: IContactRespSchema | IAddressSchema,
): boolean {
  if (!contactOrAddress) {
    return true;
  }
  if ('address' in contactOrAddress) {
    return isMissingAddressFields(contactOrAddress.address);
  }
  return isMissingAddressFields(contactOrAddress);
}

function getFormContact(
  contact?: IContactRespSchema,
  addressOverride?: IAddressSchema,
): QuoteShareContactRequiredAddress {
  const address = addressOverride ?? contact?.address;
  return {
    id: contact?.id,
    fullName: contact?.fullName || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    title: contact?.title || '',
    address: {
      line1: address?.line1 || '',
      line2: address?.line2 || '',
      city: address?.city || '',
      state: address?.state || '',
      postalCode: address?.postalCode || '',
      country: address?.country || '',
    },
  };
}

function getPaymentMethodBillingDetailsFromContact(
  contact?: IContactRespSchema,
  addressOverride?: IAddressSchema,
): IPaymentMethodContactSchema {
  const address = addressOverride ?? contact?.address;
  return {
    fullName: contact?.fullName || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    title: contact?.title || '',
    line1: address?.line1 || '',
    line2: address?.line2 || '',
    city: address?.city || '',
    state: address?.state || '',
    postalCode: address?.postalCode || '',
    country: address?.country || '',
  };
}

export function getFormFromResponse(
  requireAddress: boolean,
  requirePaymentMethod: boolean,
  allowedPaymentMethods: AllowedPaymentMethods,
  quoteData?: QuoteShareResponse,
): QuoteShareForm {
  // Ensure country is normalized if at all possible to ensure the form dropdown works correctly
  if (quoteData?.quote?.billingContact?.address?.country) {
    quoteData.quote.billingContact.address.country =
      getCountryCodeFromNameOrCode(
        quoteData.quote.billingContact.address.country,
      );
  }

  if (quoteData?.quote?.shippingContact?.address?.country) {
    quoteData.quote.shippingContact.address.country =
      getCountryCodeFromNameOrCode(
        quoteData.quote.shippingContact.address.country,
      );
  }

  if (quoteData?.quote?.billingAddress?.country) {
    quoteData.quote.billingAddress.country = getCountryCodeFromNameOrCode(
      quoteData.quote.billingAddress.country,
    );
  }

  if (quoteData?.quote?.shippingAddress?.country) {
    quoteData.quote.shippingAddress.country = getCountryCodeFromNameOrCode(
      quoteData.quote.shippingAddress.country,
    );
  }

  return {
    config: {
      requireAddress: !!requireAddress,
      allowPoNumber: !!quoteData?.allowPoNumber,
    },
    purchaseOrderNumber: quoteData?.quote?.purchaseOrderNumber || '',
    contacts: getFormContactsFromResponse(requireAddress, quoteData),
    payment: getFormPaymentMethodFromResponse(
      requirePaymentMethod,
      allowedPaymentMethods,
      quoteData,
    ),
  };
}

export function getFormContactsFromResponse(
  requireAddress: boolean,
  quoteData?: QuoteShareResponse,
): QuoteShareContactsForm {
  const isAccountBased =
    quoteData?.quote.addressSource ===
    QuoteSettingsDefaultAddressSourceEnum.ACCOUNT;

  return {
    requireAddress: !!requireAddress,
    billingContact: getFormContact(
      quoteData?.quote.billingContact,
      isAccountBased ? quoteData?.quote.billingAddress : undefined,
    ),
    shippingContact: getFormContact(
      quoteData?.quote.shippingContact,
      isAccountBased ? quoteData?.quote.shippingAddress : undefined,
    ),
    useBillingForShippingContact:
      quoteData?.quote.billingContact.id ===
      quoteData?.quote.shippingContact.id,
  };
}

export function getFormPaymentMethodFromResponse(
  requirePaymentMethod: boolean,
  allowedPaymentMethods: AllowedPaymentMethods,
  quoteData?: QuoteShareResponse,
): QuoteSharePaymentMethodForm {
  let payment: QuoteSharePaymentMethodForm = {
    paymentGatewayId: null,
    paymentMethodType: 'new',
    paymentMethod: null,
    paymentMethodId: null,
    paymentType: allowedPaymentMethods === 'ELECTRONIC' ? 'ELECTRONIC' : 'ACH',
  };

  if (requirePaymentMethod && quoteData?.paymentGatewayId) {
    const isAccountBased =
      quoteData.quote.addressSource ===
      QuoteSettingsDefaultAddressSourceEnum.ACCOUNT;

    payment = {
      paymentGatewayId: quoteData.paymentGatewayId,
      paymentMethodType:
        quoteData.paymentMethods.length > 0 ? 'existing' : 'new',
      paymentMethodId:
        quoteData.paymentMethods.find((item) => item.isDefault)?.id ??
        quoteData.paymentMethods[0]?.id,
      paymentMethod: {
        amount: 0,
        paymentMethodName: '',
        billingDetails: getPaymentMethodBillingDetailsFromContact(
          quoteData.quote.billingContact,
          isAccountBased ? quoteData.quote.billingAddress : undefined,
        ),
      },
      // Default visible payment type
      paymentType:
        allowedPaymentMethods === 'ELECTRONIC' ? 'ELECTRONIC' : 'ACH',
    };
  }
  return payment;
}
